"use client";

import posthog from "posthog-js";
import { useActionState, useEffect, useRef, useState } from "react";
import {
  subscribeToJobAlerts,
  type SubscribeActionResult,
} from "@/app/job-alerts/actions";
import { MicroSurvey, shouldAskMicroSurvey } from "@/components/MicroSurvey";

/**
 * Free job-alert email capture (CM-3) — the frictionless freemium funnel.
 * Email + a few optional filter chips (sector / region / grade), no account.
 * On submit the server stores the subscriber durably; sending stays gated off.
 *
 * PostHog funnel (measurable from day one):
 *   job_alert_cta_view    — the capture scrolled into view (once)
 *   job_alert_submit      — the form was submitted
 *   job_alert_success     — the subscription was stored
 */

const SECTORS = [
  "Health & Medical",
  "Education",
  "Environment & Climate",
  "Protection & Human Rights",
  "Humanitarian & Emergency",
  "Economic Development & Trade",
  "Migration & Refugees",
  "Food Security & Nutrition",
] as const;

const REGIONS = [
  "Africa",
  "Americas",
  "Asia-Pacific",
  "Europe",
  "MENA",
  "Global/Remote",
] as const;

const GRADES = ["P", "D", "NO", "G"] as const;

type Group = { param: string; label: string; options: readonly string[] };

const GROUPS: Group[] = [
  { param: "cat", label: "Sector", options: SECTORS },
  { param: "region", label: "Region", options: REGIONS },
  { param: "grade", label: "Grade", options: GRADES },
];

const GRADE_LABEL: Record<string, string> = {
  P: "Professional (P)",
  D: "Director (D)",
  NO: "National (NO)",
  G: "General (G)",
};

export function JobAlertCapture({
  surface = "page",
  className = "",
}: {
  /** Where this instance lives — sent with events for funnel segmentation. */
  surface?: string;
  className?: string;
}) {
  const [result, formAction, pending] = useActionState<
    SubscribeActionResult | null,
    FormData
  >(subscribeToJobAlerts, null);

  const [selected, setSelected] = useState<Record<string, Set<string>>>({});
  const [askSurvey, setAskSurvey] = useState(false);
  const [email, setEmail] = useState("");
  const [prefilled, setPrefilled] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const viewed = useRef(false);
  const succeeded = useRef(false);

  // "kept me repeating things" (exit feedback, 2026-09-30) — PostApplyAlert,
  // higher up this same page, asks for an email too. Prefill from whatever
  // was typed there instead of asking cold a second time in one visit.
  useEffect(() => {
    try {
      const saved = localStorage.getItem("cm.lastemail.v1");
      if (saved) {
        setEmail(saved);
        setPrefilled(true);
      }
    } catch {
      // Private mode / storage disabled — form just asks cold, as before.
    }
  }, []);

  // CTA view — fire once when the capture first enters the viewport.
  useEffect(() => {
    const el = rootRef.current;
    if (!el || viewed.current) return;
    const fire = () => {
      if (viewed.current) return;
      viewed.current = true;
      if (posthog.__loaded)
        posthog.capture("job_alert_cta_view", { surface });
    };
    if (typeof IntersectionObserver === "undefined") {
      fire();
      return;
    }
    const io = new IntersectionObserver(
      (entries) => entries.some((e) => e.isIntersecting) && fire(),
      { threshold: 0.3 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [surface]);

  // Success — fire once when the server confirms the subscription is stored.
  useEffect(() => {
    if (result?.ok && result.state === "subscribed" && !succeeded.current) {
      succeeded.current = true;
      if (posthog.__loaded)
        posthog.capture("job_alert_success", {
          surface,
          already_confirmed: !!result.alreadyConfirmed,
          filter_groups: Object.values(selected).filter((s) => s.size > 0)
            .length,
        });
      // Subscribing is the highest-intent moment in the free funnel and needs
      // no account — the best place we have to actually hear from a user.
      if (shouldAskMicroSurvey()) setAskSurvey(true);
    }
  }, [result, surface, selected]);

  const toggle = (param: string, value: string) =>
    setSelected((prev) => {
      const next = new Set(prev[param] ?? []);
      next.has(value) ? next.delete(value) : next.add(value);
      return { ...prev, [param]: next };
    });

  const done = result?.ok && result.state === "subscribed";

  if (done) {
    return (
      <>
      {askSurvey ? (
        <MicroSurvey
          surveyId="alert_signup_intent"
          question="What were you hoping to find today?"
          placeholder="e.g. remote M&E roles at UN agencies, P-3 and up"
          onClose={() => setAskSurvey(false)}
        />
      ) : null}
      <div
        ref={rootRef}
        className={`rounded-xl border-2 border-ink bg-white p-6 sm:p-8 ${className}`}
      >
        <p className="telex flex items-center gap-2 text-brand-700">
          <span aria-hidden className="inline-block h-[7px] w-[7px] bg-current" />
          {result?.alreadyConfirmed ? "Alert is on" : "One click left"}
        </p>
        <h3 className="display-calm mt-3 text-2xl font-semibold leading-tight text-ink">
          {result?.alreadyConfirmed
            ? "Your alert is already on."
            : "Check your inbox."}
        </h3>
        <p className="mt-3 text-[15px] leading-relaxed text-neutral-600">
          {result?.alreadyConfirmed
            ? "We're already watching this search for you — new UN, NGO and international job matches land in your inbox as they go live."
            : "We sent you a one-click link to switch your alert on. No password, no account — and you can unsubscribe from any email."}
        </p>
        <a
          href="/jobs"
          className="mt-6 inline-flex h-11 items-center justify-center rounded-md bg-brand-600 px-5 text-sm font-semibold text-white transition-colors hover:bg-brand-700"
        >
          Browse live vacancies
        </a>
      </div>
      </>
    );
  }

  return (
    <div
      ref={rootRef}
      className={`rounded-xl border-2 border-ink bg-white p-6 sm:p-8 ${className}`}
    >
      <p className="telex flex items-center gap-2 text-brand-700">
        <span aria-hidden className="inline-block h-[7px] w-[7px] bg-current" />
        Free job alerts
      </p>
      <h3 className="display-calm mt-3 text-2xl font-semibold leading-tight text-ink sm:text-3xl">
        Get new UN &amp; NGO jobs by email.
      </h3>
      <p className="mt-3 max-w-xl text-[15px] leading-relaxed text-neutral-600">
        Fresh vacancies from across the UN system, development banks, EU
        institutions and INGOs — straight to your inbox. Free, no account,
        unsubscribe anytime.
      </p>

      <form
        action={formAction}
        onSubmit={() => {
          if (posthog.__loaded)
            posthog.capture("job_alert_submit", {
              surface,
              filter_groups: Object.values(selected).filter((s) => s.size > 0)
                .length,
            });
        }}
        className="mt-6"
      >
        <label className="block">
          <span className="block text-[13px] font-medium text-ink">
            Email address
          </span>
          <input
            type="email"
            name="email"
            required
            autoComplete="email"
            placeholder="you@example.org"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1.5 w-full rounded-md border border-neutral-300 bg-white px-3.5 py-2.5 text-[15px] text-ink placeholder:text-neutral-400 focus:border-brand-600 focus:outline-none focus:ring-1 focus:ring-brand-600 sm:max-w-md"
          />
          {prefilled ? (
            <span className="mt-1 block text-[12px] text-neutral-400">
              Filled in from the email you just gave above — change it if
              you&apos;d rather use another address.
            </span>
          ) : null}
        </label>

        <fieldset className="mt-5">
          <legend className="text-[13px] font-medium text-ink">
            Filter your alerts{" "}
            <span className="font-normal text-neutral-400">
              (optional — leave empty for all jobs)
            </span>
          </legend>
          <div className="mt-3 space-y-3">
            {GROUPS.map((g) => (
              <div key={g.param}>
                <p className="telex mb-1.5 text-[8.5px] text-neutral-400">
                  {g.label}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {g.options.map((opt) => {
                    const on = selected[g.param]?.has(opt) ?? false;
                    const label =
                      g.param === "grade" ? (GRADE_LABEL[opt] ?? opt) : opt;
                    return (
                      <button
                        type="button"
                        key={opt}
                        aria-pressed={on}
                        onClick={() => toggle(g.param, opt)}
                        className={`inline-flex h-8 items-center rounded-full border px-3 text-[13px] font-medium transition-colors ${
                          on
                            ? "border-brand-600 bg-brand-600 text-white"
                            : "border-neutral-300 bg-white text-neutral-600 hover:border-brand-400 hover:text-ink"
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </fieldset>

        {/* Selected chips as real form values so the server receives them. */}
        {GROUPS.flatMap((g) =>
          [...(selected[g.param] ?? [])].map((v) => (
            <input
              key={`${g.param}:${v}`}
              type="hidden"
              name={g.param}
              value={v}
            />
          )),
        )}

        {result && !result.ok ? (
          <p
            role="alert"
            className="mt-4 rounded-md border border-stamp-100 bg-stamp-50 px-3 py-2 text-sm text-stamp-700"
          >
            {result.message}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={pending}
          className="mt-6 inline-flex h-11 w-full items-center justify-center rounded-md bg-brand-600 px-6 text-sm font-semibold text-white transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
        >
          {pending ? "Signing you up…" : "Email me new jobs"}
        </button>
        <p className="telex mt-3 text-[8.5px] text-neutral-400">
          We only store your email + filters ·{" "}
          <a href="/privacy" className="underline hover:text-brand-700">
            privacy
          </a>
        </p>
      </form>
    </div>
  );
}
